import { z } from 'zod';
import { SEASON_ID_PATTERN } from '../constants/season.js';
import { characterSchema } from './character.js';
/**
 * ランキングの 1 行（企画書 §26、[ADR-012](../../../../docs/adr/012-ranking-by-tournament-points.md)）。
 *
 * **ポイントをキャラクターに生やさない。** ポイントは「どの期間で数えたか」で変わる
 * （シーズンは日付範囲の集計。[ADR-013](../../../../docs/adr/013-seasons-as-date-ranges.md)）ので、
 * キャラクターの属性ではなく**順位表の属性**である。
 */
export const rankingEntrySchema = z.object({
    /**
     * 順位。**同点は同じ順位で、次は飛ばす**（1, 2, 2, 4）。
     *
     * サーバーが付ける。配列の添字（`i + 1`）だと、同点のときに嘘の順位になる。
     */
    rank: z.number().int().positive(),
    /**
     * **そのシーズンの**（#284。日本時間の暦の 1 か月に作られた）**ワールドカップの**順位ポイントの合計。配点は
     * [ADR-023](../../../../docs/adr/023-tournament-progression.md)。
     *
     * 並ぶのは**ワールドカップに 1 回以上出た選手だけ**（ADR-032 B6。ADR-012 の「0 の選手も出す」を改めた）。
     * 出ている大会がまだ終わっていなければ 0 のことがある（ポイントは負けた時点で確定する）。
     */
    points: z.number().int().nonnegative(),
    character: characterSchema,
});
/**
 * シーズンの ID（`YYYY-MM`。日本時間の暦の月。#284）。区切りの計算は `constants/season.ts`。
 */
export const seasonIdSchema = z
    .string()
    .regex(SEASON_ID_PATTERN, 'シーズンは YYYY-MM の形で指定してください');
/**
 * ランキングのシーズン（#284、[ADR-013](../../../../docs/adr/013-seasons-as-date-ranges.md)）。
 *
 * **アプリは区切りを計算しない。** のこりの日数などは、この `startsAt` / `endsAt` から出す（端末の時計や
 * 時間帯で区切りがずれない）。
 */
export const rankingSeasonSchema = z.object({
    id: seasonIdSchema,
    /** 始まり（日本時間の 1 日 0:00）。ISO 8601。 */
    startsAt: z.iso.datetime(),
    /** 終わり（日本時間の翌月 1 日 0:00）。**この時刻は含まない。** */
    endsAt: z.iso.datetime(),
});
/**
 * `GET /api/v1/rankings` の応答（#284）。**そのシーズンに作られたワールドカップ**のポイントだけで並べる。
 *
 * 並ぶのは、そのシーズンにワールドカップに 1 回以上出た選手だけ（ADR-032 B6 をシーズンごとに当てはめる）。
 */
export const rankingsResponseSchema = z.object({
    season: rankingSeasonSchema,
    entries: z.array(rankingEntrySchema),
});
/**
 * `GET /api/v1/rankings` のクエリ（#284）。`season` が無ければ今のシーズン。
 *
 * **今より先の月はここでは弾けない**（今の時刻が要る）。サービスが 400 にする。
 */
export const rankingsQuerySchema = z.object({
    season: seasonIdSchema.optional(),
});
